import { useEffect, useRef, useState } from "react";
import * as signalR from "@microsoft/signalr";
import API from "@src/API.js";

/**
 * Connects to Azure SignalR via authenticated negotiate.
 * On dashboardChanged, debounces and calls onRefresh().
 * Degrades gracefully when SignalR is not configured.
 */
export default function useDashboardSignalR(onRefresh) {
  const [liveStatus, setLiveStatus] = useState("connecting"); // connecting | live | unavailable
  const onRefreshRef = useRef(onRefresh);
  const debounceRef = useRef(null);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    let cancelled = false;
    let connection = null;

    function scheduleRefresh() {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (!cancelled && typeof onRefreshRef.current === "function") {
          onRefreshRef.current();
        }
      }, 400);
    }

    async function start() {
      const token = window.sessionStorage.getItem("BearerToken");
      if (!token) {
        setLiveStatus("unavailable");
        return;
      }

      const hubUrl = API.URL("/Realtime/Dashboard");
      connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: () => window.sessionStorage.getItem("BearerToken") || "",
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Warning)
        .build();

      connection.on("dashboardChanged", () => {
        scheduleRefresh();
      });

      connection.onreconnected(() => {
        if (!cancelled) {
          setLiveStatus("live");
          scheduleRefresh();
        }
      });

      connection.onclose(() => {
        if (!cancelled) setLiveStatus("unavailable");
      });

      try {
        await connection.start();
        if (!cancelled) setLiveStatus("live");
      } catch (err) {
        console.warn("Dashboard SignalR unavailable", err);
        if (!cancelled) setLiveStatus("unavailable");
      }
    }

    start();

    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (connection) {
        connection.stop().catch(() => {});
      }
    };
  }, []);

  return { liveStatus };
}
